import { type ClipboardEvent, type FC, type KeyboardEvent, type MutableRefObject, useEffect, useRef } from 'react';
import { Check, CircleCheck, EarthLock, EllipsisVertical, Loader2, Send } from 'lucide-react';
import {
  focusElementWithoutScroll,
  insertTextAtCursor,
  moveCaretToEnd,
  scrollCaretIntoView,
  scrollElementIntoViewOnNextFrame,
  syncCommentEditorValueIfBlurred,
} from '@/lib/comments';
import { CommentAuthorAndTimestamp } from '@/components/inspector/comments/comment-author-and-timestamp';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DeleteCommentMenuItem, EditCommentMenuItem } from '@/components/menu-items/node-menu-items';
import { useChangeNodeCommentResolution } from '@/hooks/comments/useChangeNodeCommentResolution';
import { CommentCharacterCount } from '@/components/inspector/comments/comment-character-count';
import { type CommentAuthor, type Comment as CommentData } from '@/types/comments';
import { MenuProvider, dropdownPrimitives } from '@/components/menu-renderer';
import { EmojiPickerComponent } from '@/components/emoji-picker-component';
import { CommentBody } from '@/components/inspector/comments/comment-body';
import { useFormatTimestamp } from '@/hooks/useFormatTimestamp';
import { Label, LabelVariants } from '@/components/ui/label';
import { useCapabilities } from '@/hooks/useCapabilities';
import { UserAvatar } from '@/components/ui/user-avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import router from 'next/router';

/**
 * Comment actions stay hidden until the thread is engaged: hovered, keyboard-focused, with an
 * overlay open inside it, or pinned by the parent. `[data-state=open]` matches any Radix trigger
 * whose overlay is open — the options menu, and the editor's emoji picker — which is deliberate:
 * the pointer is on that trigger anyway, so hover has already revealed the row, and keeping it up
 * while the overlay is used is the same intent as the pinning below. Tooltips are excluded for
 * free, since Radix gives those `delayed-open` / `instant-open` rather than `open`.
 *
 * `data-actions-pinned` is set on the group by `Comment` while a dialog this comment opened has
 * taken the pointer away; a portalled dialog is outside the group, so `group-has-*` cannot see it.
 *
 * Focus is deliberately read off the button itself rather than the group: one `group/comment`
 * spans a root comment and all its replies, so a `group-focus-within` would treat the editor's
 * caret as engagement and strand every sibling's actions on screen for the whole edit session.
 *
 * While a mutation is in flight every action is `disabled`, and `buttonVariants` fades disabled
 * buttons to 50%; `opacity-100!` at those call sites keeps the row put until the change settles
 * instead of letting it vanish mid-mutation. `disabled` already blocks interaction, so the
 * in-flight state needs no pointer-events handling of its own.
 */
const REVEALED_COMMENT_ACTION = [
  'pointer-events-none opacity-0 transition-opacity duration-150',
  'group-hover/comment:pointer-events-auto group-hover/comment:opacity-100',
  'focus-visible:pointer-events-auto focus-visible:opacity-100',
  'group-has-[[data-state=open]]/comment:pointer-events-auto group-has-[[data-state=open]]/comment:opacity-100',
  'group-data-[actions-pinned]/comment:pointer-events-auto group-data-[actions-pinned]/comment:opacity-100',
].join(' ');

/**
 * Collapse to zero width on top of `REVEALED_COMMENT_ACTION`, for actions that sit in the author
 * row: `CommentAuthorAndTimestamp` grows to fill that row, so a hidden-but-present button would eat
 * width from the name and truncate it permanently. Expands on the same triggers that reveal it.
 *
 * Clamping with `max-w-*` rather than `w-0` keeps it deterministic: `w-0` only ties with the
 * `main-toolbar` size variant's `size-7` on specificity, so the winner would come down to
 * stylesheet order. Height is left alone, so the button wipes in horizontally instead of growing
 * out of the corner.
 *
 * `min-w-0` and `shrink-0` are both load-bearing. Min-width outranks max-width, so the variant's
 * `min-w-7` — and the `min-width: auto` that flex items fall back to, which resolves to the icon's
 * content size — would otherwise floor the collapsed button open. Dropping that floor is also what
 * lets it shrink under a long author name, so `shrink-0` pins the expanded button at a full 28px.
 */
const COLLAPSED_COMMENT_ACTION = [
  'max-w-0 min-w-0 shrink-0 overflow-hidden',
  'group-hover/comment:max-w-7',
  'focus-visible:max-w-7',
  'group-has-[[data-state=open]]/comment:max-w-7',
  'group-data-[actions-pinned]/comment:max-w-7',
].join(' ');

export const CommentItem: FC<{
  comment: CommentData;
  index?: number;
  nodeId: string;
  rootMeta?: {
    commentId: string;
    resolvedAt: string | null;
    resolvedBy: CommentAuthor | null;
  };
  isReply?: boolean;
  readOnly?: boolean;
  onReply?: () => void;
  repliesLength: number;
  showMore?: boolean;
  canEdit: boolean;
  canDelete?: boolean;
  canResolve?: boolean;
  isDraftReply?: boolean;
  /**
   * Hidden from contributors. Privacy is a property of the thread, not the individual comment:
   * only the root carries the `private` flag, and every reply under a private root is hidden with
   * it — so replies are given their root's value rather than their own (always absent) one.
   */
  isPrivate?: boolean;
  /**
   * Tombstone UX in digital design refers to the practice of showing a placeholder
   * or UI skeleton (often blank or greyed-out boxes) to represent content
   * that is loading, deleted, or unavailable.
   **/
  isTombstone?: boolean;
  deleteCommentId?: string;
  parentCommentId?: string;
  onDeleteStart?: () => void;
  onDeleteSuccess?: (deletedCommentId: string) => void;
  /** Reports the modal key of any confirmation opened from this item, so the thread can pin its actions. */
  onDialogOpen?: (modalKey: string) => void;
  onTogglePrivacy?: () => void;
  isEditing: boolean;
  isMenuOpen: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  normalizedDraft: string;
  isDraftEmpty: boolean;
  isDraftUnchanged: boolean;
  itemRef?: MutableRefObject<HTMLDivElement | null>;
  scrollContainerRef?: MutableRefObject<HTMLDivElement | null>;
  editorRef: MutableRefObject<HTMLDivElement | null>;
  onMenuOpenChange: (isOpen: boolean) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditorInput: (element: HTMLDivElement) => void;
  onEditorPaste: (event: ClipboardEvent<HTMLDivElement>) => void;
  onEmojiInsert?: (element: HTMLDivElement) => void;
}> = ({
  comment,
  index,
  nodeId,
  rootMeta,
  isReply = true,
  readOnly,
  onReply,
  repliesLength,
  showMore,
  canEdit,
  canDelete,
  canResolve = true,
  isDraftReply = false,
  isPrivate = false,
  isTombstone = false,
  deleteCommentId,
  parentCommentId,
  onDeleteStart,
  onDeleteSuccess,
  onDialogOpen,
  onTogglePrivacy,
  isEditing,
  isMenuOpen,
  isSaving,
  isDeleting,
  normalizedDraft,
  isDraftEmpty,
  isDraftUnchanged,
  itemRef,
  scrollContainerRef,
  editorRef,
  onMenuOpenChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditorInput,
  onEditorPaste,
  onEmojiInsert,
}) => {
  const tComments = useTranslations('inspector.comments');
  const tCommon = useTranslations('common');
  const { canCommentPrivately } = useCapabilities();
  const hasInitializedEditorRef = useRef(false);
  const selected = (router.query.commentId as string | null) === comment.id;
  const { formatTimestampFromNow } = useFormatTimestamp();

  // Show thread line on root comment if there are replies, or on replies if it's not the last reply
  const shouldShowThreadLine = (isReply && index !== repliesLength - 1) || (!isReply && repliesLength > 0);

  const itemType = isReply ? 'reply' : 'comment';
  const commentResolved = Boolean(rootMeta?.resolvedAt);
  const ResolvedIcon = commentResolved ? CircleCheck : Check;
  const areActionsDisabled = isSaving || isDeleting || isTombstone;
  // Replies are never private. Any non-guest, non-anonymous user can toggle any root comment's
  // privacy — unlike edit/delete, this isn't restricted to the comment's own author.
  const canTogglePrivacy = !isReply && canCommentPrivately && Boolean(onTogglePrivacy);

  const { changeNodeCommentResolutionMutation } = useChangeNodeCommentResolution(nodeId);
  const isResolvingComment = changeNodeCommentResolutionMutation.isPending;

  useEffect(() => {
    if (!isEditing) {
      hasInitializedEditorRef.current = false;
      return;
    }

    if (!editorRef.current || hasInitializedEditorRef.current) {
      return;
    }

    editorRef.current.textContent = normalizedDraft;
    focusElementWithoutScroll(editorRef.current);
    moveCaretToEnd(editorRef.current);
    scrollCaretIntoView(editorRef.current, { defer: true });
    scrollElementIntoViewOnNextFrame(itemRef?.current ?? editorRef.current, {
      container: scrollContainerRef?.current,
      // Add top margin only when editing root comment, not replies
      ...(!isReply && {
        marginTop: 12,
      }),
      // Add bottom margin only when editing last reply or editing root comment without replies
      ...(((isReply && index === repliesLength - 1) || (!isReply && repliesLength === 0)) && {
        marginBottom: 12,
      }),
    });
    hasInitializedEditorRef.current = true;
  }, [editorRef, isEditing, isReply, itemRef, normalizedDraft, scrollContainerRef, repliesLength, index]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    syncCommentEditorValueIfBlurred({
      editorRef,
      nextValue: normalizedDraft,
    });
  }, [editorRef, isEditing, normalizedDraft]);

  const handleEditorKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onSaveEdit();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onCancelEdit();
    }
  };

  return (
    <div
      id={isReply ? comment.id : undefined}
      ref={itemRef}
      className={cn('flex gap-3 p-3', {
        'bg-feature-sharing/20': isPrivate && (isEditing || selected),
        'bg-ui-medium': !isPrivate && (isEditing || selected),
        // Listed last so they win the `bg-*` merge: the reply composer counts as editing, but the
        // draft it renders is not part of the thread yet and keeps the resting surface — grey on a
        // public thread, and a lighter tint of the thread's own colour on a private one.
        'bg-ui-panel': isDraftReply && !isPrivate,
        'bg-feature-sharing/10': isDraftReply && isPrivate,
        'opacity-50': (isReply && isDeleting) || isTombstone,
      })}
      data-cy={isDraftReply ? 'comment-draft-reply-item' : isReply ? 'comment-reply-item' : 'comment-root-item'}
    >
      <div className="relative flex w-6 shrink-0 flex-col items-center">
        <UserAvatar className="z-10 size-6!" user={comment?.createdBy ?? { id: 'Deleted', name: 'Deleted' }} />
        {shouldShowThreadLine && (
          <div
            className={cn('absolute top-2 z-0 w-0.5 bg-ui-separator', {
              'h-[calc(100%+40px)]': showMore,
              'h-[calc(100%+25px)]': !showMore,
            })}
            data-cy="comment-thread-line"
          />
        )}
      </div>

      <div className="flex grow flex-col gap-2">
        <div className="flex gap-x-2">
          <CommentAuthorAndTimestamp comment={comment} isDraftReply={isDraftReply} />
          {canTogglePrivacy && !isDeleting && (
            <Button
              variant="main-toolbar"
              size="main-toolbar"
              className={cn(
                '-mt-1 rounded-full',
                // Editing repaints the item `bg-ui-medium`, which `bg-ui-base` sits 2% away from in
                // light mode and *below* in dark mode. Fall back to the same translucent overlay
                // `buttonVariants` uses for focus, which highlights against any surface.
                isEditing ? 'hover:bg-black/4 dark:hover:bg-white/4' : 'hover:bg-ui-base',
                // Public is the default state and carries no badge, so the toggle stays collapsed
                // there until the thread is engaged. On a private comment it doubles as a
                // permanent badge — the coloured lock is the only thing marking the comment as
                // hidden — so it never collapses.
                !isPrivate && [REVEALED_COMMENT_ACTION, COLLAPSED_COMMENT_ACTION],
                // After the recipes, so it beats the `transition-opacity` in REVEALED and the width
                // animates too. Unconditional: the private badge has a hover background to ease.
                'transition-all duration-150',
                areActionsDisabled && 'max-w-7 opacity-100!',
              )}
              icon={EarthLock}
              iconClassName={cn('icon-5', isPrivate ? 'stroke-feature-sharing!' : 'stroke-icon-base')}
              onClick={() => onTogglePrivacy?.()}
              disabled={areActionsDisabled}
              tooltip={isPrivate ? tComments('makeCommentVisibleTooltip') : tComments('hideCommentTooltip')}
              data-cy="comments-private-toggle"
            />
          )}
        </div>

        {isEditing ? (
          <div className="flex flex-col gap-2">
            <div
              ref={editorRef}
              className={cn(
                'min-h-14 overflow-y-auto whitespace-pre-wrap text-xs text-text-base outline-hidden wrap-anywhere focus-visible:border-highlight-base',
                isReply ? 'max-h-52' : 'max-h-64',
              )}
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-multiline="true"
              data-cy={`edit-${itemType}-input`}
              onInput={(event) => onEditorInput(event.currentTarget)}
              onPaste={onEditorPaste}
              onKeyDown={handleEditorKeyDown}
            />
            <CommentCharacterCount normalizedDraft={normalizedDraft} className="self-end" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-0.5">
                <EmojiPickerComponent
                  onEmojiSelect={(emoji) => {
                    if (editorRef.current) {
                      insertTextAtCursor(editorRef.current, emoji);
                      onEmojiInsert?.(editorRef.current);
                    }
                  }}
                  disabled={areActionsDisabled}
                />
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  onClick={onCancelEdit}
                  disabled={areActionsDisabled}
                  data-cy={`edit-${itemType}-dismiss-button`}
                  className="rounded-md bg-ui-separator px-2 text-text-inverted hover:bg-ui-bevel dark:bg-ui-separator dark:text-text-base dark:hover:bg-ui-medium"
                >
                  {tCommon('dismiss')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={onSaveEdit}
                  disabled={isDraftEmpty || isDraftUnchanged || areActionsDisabled}
                  data-cy={isDraftReply ? 'reply-submit-button' : `edit-${itemType}-save-button`}
                  className={cn(
                    'rounded-md px-2 text-text-inverted dark:text-text-inverted',
                    isPrivate
                      ? 'bg-feature-sharing dark:bg-feature-sharing/90 text-white! hover:bg-feature-sharing dark:hover:bg-feature-sharing'
                      : 'bg-highlight-base hover:bg-highlight-hover-color dark:bg-highlight-base dark:hover:bg-highlight-hover-color',
                  )}
                  {...(isSaving && { icon: Loader2 })}
                  {...(isDraftReply && !isSaving && { icon: Send })}
                  {...((isSaving || isDraftReply) && {
                    iconClassName: cn('icon-4', {
                      'animate-spin': isSaving,
                    }),
                  })}
                  {...(isDraftReply && { tooltip: tComments('submitReply') })}
                >
                  {!isSaving && !isDraftReply && tCommon('save')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <CommentBody isTombstone={isTombstone} body={comment.body} isReply={isReply} />

            {!isTombstone && (
              <div className="flex items-center justify-between gap-2">
                {!readOnly && (
                  <Button
                    variant="main-toolbar"
                    onClick={onReply}
                    disabled={isDeleting || !onReply || areActionsDisabled}
                    aria-hidden={!onReply || undefined}
                    className={cn('transition-opacity min-h-auto! p-1 -ml-1 duration-150', {
                      'pointer-events-none opacity-50': !onReply,
                    })}
                    {...(onReply && { 'data-cy': 'comment-reply-button' })}
                  >
                    <Label variant={LabelVariants.INSPECTOR_SECTION} className="mb-0 cursor-[inherit]">
                      {tComments('reply')}
                    </Label>
                  </Button>
                )}

                <div className="ml-auto flex items-center gap-x-1">
                  {!readOnly && (
                    <div className="flex icon-6 items-center justify-center">
                      {canEdit || canDelete ? (
                        <DropdownMenu open={isMenuOpen} onOpenChange={onMenuOpenChange}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="main-toolbar"
                              size="main-toolbar"
                              tooltipDelayDuration={750}
                              disabled={areActionsDisabled}
                              icon={EllipsisVertical}
                              iconClassName="icon-5"
                              className={cn(
                                'rounded-full text-icon-base hover:bg-ui-base',
                                REVEALED_COMMENT_ACTION,
                                areActionsDisabled && 'opacity-100!',
                              )}
                              tooltip={tComments('moreOptions')}
                              tooltipClassName="text-center"
                              data-cy={`${itemType}-options-button`}
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent onCloseAutoFocus={(event) => event.preventDefault()}>
                            <MenuProvider primitives={dropdownPrimitives}>
                              {canEdit && <EditCommentMenuItem onSelect={onStartEdit} />}
                              {canDelete && deleteCommentId && (
                                <DeleteCommentMenuItem
                                  comment={{ nodeId, commentId: deleteCommentId }}
                                  isReply={isReply}
                                  hasReplies={repliesLength > 0}
                                  parentCommentId={parentCommentId}
                                  onDeleteStart={onDeleteStart}
                                  onSuccess={onDeleteSuccess}
                                  onConfirmationOpen={onDialogOpen}
                                  disabled={areActionsDisabled}
                                />
                              )}
                            </MenuProvider>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <div aria-hidden className="size-6" />
                      )}
                    </div>
                  )}
                  {!isReply && rootMeta && (canResolve || commentResolved) && (
                    <Button
                      variant="main-toolbar"
                      size="main-toolbar"
                      tooltipDelayDuration={750}
                      disabled={isResolvingComment || areActionsDisabled || !canResolve}
                      icon={ResolvedIcon}
                      iconClassName={cn('icon-5', commentResolved ? 'stroke-highlight-base!' : 'stroke-icon-base')}
                      tooltipClassName="text-balance max-w-50 break-words whitespace-normal text-center"
                      data-cy="comment-resolve-button"
                      tooltip={
                        commentResolved
                          ? tComments.rich('resolvedBy', {
                              name: rootMeta.resolvedBy?.name ?? 'a deleted user',
                              ago: formatTimestampFromNow(rootMeta.resolvedAt!),
                            })
                          : tComments('resolveComment')
                      }
                      onClick={() => {
                        changeNodeCommentResolutionMutation.mutate({
                          commentId: rootMeta.commentId,
                          resolve: !commentResolved,
                        });
                      }}
                      className={cn(
                        'rounded-full hover:bg-ui-base',
                        // A resolved comment shows its state permanently; an unresolved one only
                        // offers the action while the thread is engaged.
                        // Important: a viewer who cannot unresolve gets a `disabled` button, and
                        // `buttonVariants`' `disabled:opacity-50` outranks a plain `opacity-100`
                        // on specificity — the resolved marker would render half-faded.
                        commentResolved ? 'opacity-100!' : REVEALED_COMMENT_ACTION,
                        commentResolved && !canResolve && 'cursor-default!',
                        areActionsDisabled && 'opacity-100!',
                      )}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
